import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import { GROUPBY } from '../../../constants/Reports';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { useSelector } from 'react-redux';
import {
  selectCSVWoburnPTOReport,
  selectEmployeeData,
  selectWoburnPTOReport,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import {
  getCSVWoburnPTOReportData,
  getEmployeeList,
  getWoburnPTOReportData,
  setWoburnPtoCSVReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import { useColumns } from './hooks/useColumns';
import { useRows } from './hooks/useRows';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReusableGrid from '../../../components/Grid';

const defaultFormValues = {
  emp_code: { id: 'ALL', name: 'All' },
  groupby: { id: 'Emp', name: 'Employee' },
};

const WoburnPTOReport = () => {
  const dispatch = useDispatch();
  const { fromDate, toDate, render } = useDateRangeFilter();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });
  const { emp_code: employees, groupby } = watch();

  const { woburnPTOdata, isWoburnPTODataLoading } = useSelector(
    selectWoburnPTOReport
  );
  const { woburnPtoCSVData, isWoburnPtoCSVDataLoading } = useSelector(
    selectCSVWoburnPTOReport
  );

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp: employees.id,
    };
  }, [fromDate, toDate, groupby, employees]);

  useEffect(
    () => {
      dispatch(getWoburnPTOReportData(param));
      dispatch(getEmployeeList());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleFindClick = () => dispatch(getWoburnPTOReportData(param));
  const handleDownloadReport = () => {
    const query = { ...param, csv: 1 };
    dispatch(getCSVWoburnPTOReportData(query));
  };

  useEffect(() => {
    if (woburnPtoCSVData.data) {
      saveCsvToFile(woburnPtoCSVData.data, woburnPtoCSVData.filename);
      dispatch(setWoburnPtoCSVReportData({ data: null, filename: '' }))
    }
  }, [woburnPtoCSVData, dispatch]);

  const columns = useColumns();
  const rows = useRows(woburnPTOdata.data, columns);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          enableAllOption
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple={false}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isWoburnPtoCSVDataLoading}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Woburn PTO Report Details" />

      <Box xs={12} className="d-flex justify-content-between">
        <Box sx={{ width: '100%', mr: 2 }}>
          <Grid container sx={{ width: '100%', mb: '24px' }}>
            <FilterPanel
              filters={filterConfig}
              columns={isTabletVerticalWidth ? 1 : 4}
            />
          </Grid>

          <Grid container sx={{ width: '100%' }}>
            <Grid
              item
              sx={{
                display: 'flex',
                gap: 2,
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: isTabletVerticalWidth ? 'column' : 'row',
                  gap: 2,
                  width: '100%',
                  mt: 'auto',
                }}
              >
                {render}
              </Grid>
            </Grid>
          </Grid>

          {isWoburnPTODataLoading ? (
            <Box
              sx={{ minHeight: '100px' }}
              className="w-100 h-100 d-flex justify-content-center align-items-center"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {rows.length === 0 ? (
                <DataNotFound />
              ) : (
                <Box mt={4}>
                  {rows.map((item) => (
                    <Box key={item.name} mb={4}>
                      {item.title && (
                        <Typography variant="h6" fontWeight={600} mb={2}>
                          {item.title}
                        </Typography>
                      )}
                      <ReusableGrid columns={columns} rows={item.items} footer={item.footer} />
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default WoburnPTOReport;
